import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlusCircle } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { ErrorToaster } from "../components/toaster/Toaster";
import { generatePdf } from "../../utils/helper";
import dayjs from "dayjs";
import { Table } from "antd";
import SaleInvoiceServices from "../../services/SaleInvoice";
import { useSelector } from "react-redux";
import StateServices from "../../services/State";

const SaleInvoice = () => {

  const invoiceRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => {
    return {
      userData: state.user_data
    }
  });

  const storeLogo = userData?.store?.logoBase64 || '/assets/img/mattress-plus-logo.png'
  const storeQrCode = userData?.store?.qrCodeBase64 || '/assets/img/store/store-qr.png'

  // *For Loading
  const [loading, setLoading] = useState(false);

  // *For Sale Invoice Data
  const [saleInvoice, setSaleInvoice] = useState();
  const [billingDetail, setBillingDetail] = useState();
  const [shippingDetail, setShippingDetail] = useState();

  // *For Linked Invoices (Refunds/Exchanges) - now loaded with sale invoice
  const [linkedInvoices, setLinkedInvoices] = useState({ refunds: [], exchanges: [] });

  // *For Generate PDF
  const handleGeneratePdf = async () => {
    setLoading(true)
    try {
      await generatePdf([invoiceRef], 'sale-invoice')
    } catch (error) {
      console.log('handleGeneratePdf => error:', error)
    } finally {
      setLoading(false)
    }
  };

  const getInfoByZipCode = async (zipCode, type) => {
    try {
      if (zipCode.length == 5) {
        const { data } = await StateServices.getInfoByZipCode(zipCode)
        if (type == 'billing') {
          setBillingDetail(data[0])
        } else {
          setShippingDetail(data[0])
        }
      } else {
        return
      }
    } catch (error) {
      console.log(' getInfoByZipCode => error:', error)
      // ErrorToaster(error)
    }
  }

  // *For Get Sale Invoice Details
  const getSaleInvoiceDetails = async () => {
    try {
      const { data } = await SaleInvoiceServices.getSaleInvoiceById(id)
      setSaleInvoice(data[0])
      getInfoByZipCode(data[0]?.billing_zip_code, 'billing')
      getInfoByZipCode(data[0]?.shipping_zip_code, 'shipping')

      // Set linked invoices (refunds/exchanges) from the sale invoice data
      if (data[0]?.order_number && !data[0].order_number.endsWith('RET') && !data[0].order_number.endsWith('EX')) {
        setLinkedInvoices({
          refunds: data[0].refunds || [],
          exchanges: data[0].exchanges || []
        })
      }
    } catch (error) {
      ErrorToaster(error)
    }
  }



  useEffect(() => {
    if (id) {
      getSaleInvoiceDetails()
    }
  }, [id]);

  // --- Interleave fee lines after their respective items (using sale_invoice_item_id) ---
  const mergedRows = React.useMemo(() => {
    if (!saleInvoice) return [];
    const items = saleInvoice.sale_invoice_items || [];
    const fees = saleInvoice.fees || [];
    let result = [];

    // For exchange invoices, show exchange items as separate entries
    if (saleInvoice.order_number?.endsWith('EX') && linkedInvoices.exchanges.length > 0) {
      // For exchange invoices, show exchange items as separate entries
      linkedInvoices.exchanges.forEach(exchange => {
        if (exchange.items && Array.isArray(exchange.items)) {
          exchange.items.forEach(exchangeItem => {
            // Show new item
            if (exchangeItem.newItem) {
              result.push({
                id: `exchange-new-${exchangeItem.id}`,
                brand: exchangeItem.newItem.brand || { name: '' },
                item: exchangeItem.newItem,
                variant: exchangeItem.variant,
                quantity: exchangeItem.quantity,
                regular_price: '',
                sales_price: exchangeItem.new_item_price || 0,
                extended_total: (exchangeItem.new_item_price || 0) * exchangeItem.quantity,
                delivery_type: { name: '' },
                isFee: false,
                isExchangeItem: true,
                exchangeType: 'new'
              });
            }
            // Show original item being returned
            if (exchangeItem.originalItem) {
              result.push({
                id: `exchange-original-${exchangeItem.id}`,
                brand: exchangeItem.originalItem.brand || { name: '' },
                item: exchangeItem.originalItem,
                variant: exchangeItem.originalVariant,
                quantity: exchangeItem.quantity,
                regular_price: '',
                sales_price: -(exchangeItem.original_item_price || 0), // Negative for returned items
                extended_total: -((exchangeItem.original_item_price || 0) * exchangeItem.quantity),
                delivery_type: { name: '' },
                isFee: false,
                isExchangeItem: true,
                exchangeType: 'returned'
              });
            }
          });
        }
      });
    } else {
      // For regular invoices, show sale invoice items
      items.forEach(item => {
        if (item.quantity > 0) {
          result.push({ ...item, isFee: false });
          // Find fees for this item (using sale_invoice_item_id)
          const itemFees = fees.filter(fee => {
            let itemId = fee.sale_invoice_item_id;
            if (!itemId && fee.sale_invoice_item && fee.sale_invoice_item.id) {
              itemId = fee.sale_invoice_item.id;
            }
            return itemId === item.id;
          });
          itemFees.forEach(fee => {
            result.push({
              id: `fee-${fee.id}`,
              brand: { name: '' },
              item: { name: fee.description },
              quantity: fee.quantity,
              regular_price: '',
              sales_price: fee.unit_fee,
              extended_total: fee.amount,
              delivery_type: { name: '' },
              isFee: true,
            });
          });
        }
      });
    }

    // Add any fees not linked to an item at the end (only for regular invoices)
    if (!saleInvoice.order_number?.endsWith('EX')) {
      const unlinkedFees = fees.filter(fee => {
        let itemId = fee.sale_invoice_item_id;
        if (!itemId && fee.sale_invoice_item && fee.sale_invoice_item.id) {
          itemId = fee.sale_invoice_item.id;
        }
        return !items.some(item => item.id === itemId);
      });
      unlinkedFees.forEach(fee => {
        result.push({
          id: `fee-${fee.id}`,
          brand: { name: '' },
          item: { name: fee.description },
          quantity: fee.quantity,
          regular_price: '',
          sales_price: fee.unit_fee,
          extended_total: fee.amount,
          delivery_type: { name: '' },
          isFee: true,
        });
      });
    }
    return result;
  }, [saleInvoice, linkedInvoices]);

  // --- Columns: For fee rows, show only the unit fee in Sale Price column ---
  const columns = [
    {
      title: "Qty",
      dataIndex: "quantity",
      width: 45,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Description",
      dataIndex: "item",
      width: 200,
      render: (text, record) => {
        let displayName = text?.name || '';
        if (record.variant && record.variant.attributes) {
          const variantDetails = Object.values(record.variant.attributes).join('/');
          if (variantDetails) displayName += ` (${variantDetails})`;
        }
        return (
          <p style={{ wordBreak: 'break-word', whiteSpace: 'normal', margin: 0, ...(record.isFee ? { fontStyle: 'italic', color: '#ff9f43' } : {}) }}>
            {displayName}
            {record.isFee && (
              <span className="badge badge-warning ms-2" style={{ background: '#fff5ec', color: '#ff9f43', border: '1px solid #ff9f43', fontSize: 9, fontWeight: 500, padding: '0.15rem 0.35rem' }}>FEE</span>
            )}
            {record.isExchangeItem && record.exchangeType === 'returned' && (
              <span className="badge badge-danger ms-2" style={{ background: '#fff5ec', color: '#dc3545', border: '1px solid #dc3545', fontSize: 9, fontWeight: 500, padding: '0.15rem 0.35rem' }}>RETURNED</span>
            )}
            {record.isExchangeItem && record.exchangeType === 'new' && (
              <span className="badge badge-success ms-2" style={{ background: '#f8fff8', color: '#28a745', border: '1px solid #28a745', fontSize: 9, fontWeight: 500, padding: '0.15rem 0.35rem' }}>NEW</span>
            )}
          </p>
        );
      },
    },
    {
      title: "Brand",
      dataIndex: "item",
      width: 80,
      render: (text) => <span>{text?.brand?.name}</span>,
    },
    {
      title: "Size",
      dataIndex: "item",
      width: 70,
      render: (text, record) => (
        <span>{record?.sold_variants?.SIZE} {record?.sold_variants?.FIRMNESS_LEVEL}</span>
      ),
    },
    {
      title: "Reg Price",
      dataIndex: "regular_price",
      width: 80,
      render: (text) => <span>{text !== '' ? `$${parseFloat(text).toFixed(2)}` : ''}</span>,
    },
    {
      title: "Sale Price",
      dataIndex: "sales_price",
      width: 80,
      render: (text, record) => (
        record.isFee
          ? <span>{`$${parseFloat(text).toFixed(2)}`}</span>
          : <span>{text !== '' ? `$${parseFloat(text).toFixed(2)}` : ''}</span>
      ),
    },
    {
      title: "Extended Total",
      dataIndex: "extended_total",
      width: 100,
      render: (text, record) => (
        <span style={record.isFee ? { color: '#ff9f43', fontWeight: 600 } : {}}>
          {text !== '' ? `$${parseFloat(text).toFixed(2)}` : ''}
        </span>
      ),
    },
    {
      title: "Shipping",
      dataIndex: "delivery_type",
      width: 80,
      render: (text) => <span>{text?.name}</span>,
    },
  ];

  // --- Remove refund breakdown from summary, restore original summary section ---
  const refundedFeeTotal = React.useMemo(() => {
    if (!saleInvoice || !saleInvoice.refunds) return 0;
    let total = 0;
    for (const refund of saleInvoice.refunds) {
      if (refund.items && Array.isArray(refund.items)) {
        for (const refundItem of refund.items) {
          if (refundItem.fees && Array.isArray(refundItem.fees)) {
            for (const fee of refundItem.fees) {
              total += Number(fee.amount) || 0;
            }
          }
        }
      }
    }
    return total;
  }, [saleInvoice]);

  // --- Calculate total refund (items + fees) for summary ---
  const totalRefundAmount = (saleInvoice?.refund_amount || 0);
  const netTotalAfterRefund = (saleInvoice?.total || 0) - totalRefundAmount;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header d-flex align-items-center justify-content-between">
          <div className="page-title">
            <h4>View Sale Invoice</h4>
            {saleInvoice?.order_number?.endsWith('RET') && (
              <small className="text-danger d-block">
                <i className="fas fa-undo-alt me-1"></i>
                This is a Refund Invoice
              </small>
            )}
            {saleInvoice?.order_number?.endsWith('EX') && (
              <small className="text-success d-block">
                <i className="fas fa-exchange-alt me-1"></i>
                This is an Exchange Invoice
              </small>
            )}
          </div>
          <div className="d-flex align-items-center">
            {(saleInvoice?.order_number?.endsWith('RET') || saleInvoice?.order_number?.endsWith('EX')) && (
              <button
                className="btn btn-outline-primary me-2"
                onClick={() => {
                  // Navigate to sales list with original order number filter
                  const originalOrderNumber = saleInvoice.order_number.replace(/RET$|EX$/, '');
                  navigate(`/sales?orderNumber=${originalOrderNumber}`);
                }}
              >
                <i className="fas fa-arrow-left me-1"></i>
                View Original Invoice
              </button>
            )}
            <button disabled={loading} className="btn btn-added" onClick={() => handleGeneratePdf()}>
              <PlusCircle className="me-2 iconsize" />
              Print
            </button>
            {!saleInvoice?.order_number?.endsWith('RET') && !saleInvoice?.order_number?.endsWith('EX') && (
              <button className="btn btn-warning ms-2" onClick={() => navigate(`/refund-exchange/${id}`)}>
                Refund/Exchange
              </button>
            )}
          </div>
        </div>

        <div className="card-body mb-3">
          <div id='invoiceToPrint' className="card m-auto" style={{ width: '750px' }}>
            <div ref={invoiceRef}>
              <div className="card-body add-product p-3">

                <div className="row align-items-start justify-content-between">
                  <div className="col-md-5">
                    <img src={storeLogo} alt="img" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                  </div>
                  <div className="col-md-5">
                    <h3 className="mb-1">
                      {saleInvoice?.order_number?.endsWith('EX') ? 'EXCHANGE INVOICE' :
                        saleInvoice?.order_number?.endsWith('RET') ? 'REFUND INVOICE' : 'INVOICE'}
                    </h3>
                  </div>
                </div>
                <div className="row align-items-start justify-content-between mb-2">
                  <div className="col-md-5 d-flex align-items-end justify-content-between">
                    <div style={{ fontSize: 11 }}>
                      <p className="mb-0 fw-bold">{userData?.store?.name}</p>
                      <p className="mb-0">{userData?.store?.address.split(',')[0]}</p>
                      <p className="mb-0">{userData?.store?.address.split(', ').slice(1).join(', ')}</p>
                      <p className="mb-0">{userData?.store?.phone_number}</p>
                    </div>
                    <img src={storeQrCode} alt="qr" style={{ height: '60px', width: '60px', objectFit: 'contain', marginLeft: 8 }} />
                  </div>

                  <div className="col-md-5 d-flex gap-4" style={{ fontSize: 11 }}>
                    <table style={{ borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td className="pe-2 fw-bold">Purchase Date</td>
                          <td>{dayjs(saleInvoice?.order_date).format('M/D/YYYY')}</td>
                        </tr>
                        <tr>
                          <td className="pe-2 fw-bold">Order #</td>
                          <td>{saleInvoice?.order_number}</td>
                        </tr>
                        <tr>
                          <td className="pe-2 fw-bold">Delivery</td>
                          <td>{dayjs(saleInvoice?.delivery_date).format('M/D/YYYY')} {saleInvoice?.delivery_day} {saleInvoice?.delivery_time_slot?.name}</td>
                        </tr>
                        <tr>
                          <td className="pe-2 fw-bold">Salesperson</td>
                          <td>{saleInvoice?.salespersons?.map(s => s.name).join(', ')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="row justify-content-between mb-2">
                  <div className="col-md-5">
                    <h6 className="text-center py-1 mb-1" style={{ backgroundColor: 'rgb(0,176,240)', color: '#fff', fontSize: 11 }}>Billing Info</h6>
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ width: '30%', fontWeight: 600 }}>Name</td>
                          <td>{saleInvoice?.billing_name} {saleInvoice?.billing_last_name}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600 }}>Address</td>
                          <td>{saleInvoice?.billing_address}, {billingDetail?.city?.name} {billingDetail?.state?.name} {saleInvoice?.billing_zip_code}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600 }}>Phone 1</td>
                          <td>{saleInvoice?.billing_phone_number_one}</td>
                        </tr>
                        {saleInvoice?.billing_phone_number_two && (
                          <tr>
                            <td style={{ fontWeight: 600 }}>Phone 2</td>
                            <td>{saleInvoice?.billing_phone_number_two}</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ fontWeight: 600 }}>E-mail</td>
                          <td>{saleInvoice?.billing_email}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-5">
                    <h6 className="text-center py-1 mb-1" style={{ backgroundColor: 'rgb(0,176,240)', color: '#fff', fontSize: 11 }}>Delivery Info</h6>
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ width: '30%', fontWeight: 600 }}>Name</td>
                          <td>{saleInvoice?.shipping_name} {saleInvoice?.shipping_last_name}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600 }}>Address</td>
                          <td>{saleInvoice?.shipping_address}, {shippingDetail?.city?.name} {shippingDetail?.state?.name}, {saleInvoice?.shipping_zip_code}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600 }}>Phone 1</td>
                          <td>{saleInvoice?.shipping_phone_number_one}</td>
                        </tr>
                        {saleInvoice?.shipping_phone_number_two && (
                          <tr>
                            <td style={{ fontWeight: 600 }}>Phone 2</td>
                            <td>{saleInvoice?.shipping_phone_number_two}</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ fontWeight: 600 }}>E-mail</td>
                          <td>{saleInvoice?.shipping_email}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mb-2">
                  <Table
                    className="table datanew dataTable no-footer custom-table"
                    columns={columns}
                    dataSource={mergedRows}
                    pagination={false}
                  // No background color for fee rows
                  />
                  {/* Exchange/Refund Details Section */}
                  {saleInvoice?.order_number?.endsWith('EX') && (
                    <div className="row mt-3 mb-3">
                      <div className="col-12">
                        <div className="card border-success">
                          <div className="card-header bg-success text-white">
                            <h6 className="card-title mb-0">
                              <i className="fas fa-exchange-alt me-2"></i>
                              Exchange Transaction Details
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6">
                                <h6 className="text-success">Items Received</h6>
                                <p className="mb-1">This invoice shows the new items purchased in this exchange.</p>
                                <p className="mb-0 small text-muted">Store credit from returned items was applied to reduce the total cost.</p>
                              </div>
                              <div className="col-md-6">
                                <h6 className="text-info">Exchange Summary</h6>
                                <div className="small">
                                  <p className="mb-1"><strong>Original Invoice:</strong> {saleInvoice?.order_number?.replace('EX', '')}</p>
                                  <p className="mb-1"><strong>Exchange Date:</strong> {dayjs(saleInvoice?.created_at).format('MM-DD-YYYY')}</p>
                                  <p className="mb-0"><strong>Status:</strong> <span className="badge bg-success">{saleInvoice?.status}</span></p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {saleInvoice?.order_number?.endsWith('RET') && (
                    <div className="row mt-3 mb-3">
                      <div className="col-12">
                        <div className="card border-danger">
                          <div className="card-header bg-danger text-white">
                            <h6 className="card-title mb-0">
                              <i className="fas fa-undo-alt me-2"></i>
                              Refund Transaction Details
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6">
                                <h6 className="text-danger">Items Returned</h6>
                                <p className="mb-1">This invoice shows the items being returned for refund.</p>
                                <p className="mb-0 small text-muted">Processing fees and pickup charges have been deducted from the refund amount.</p>
                              </div>
                              <div className="col-md-6">
                                <h6 className="text-info">Refund Summary</h6>
                                <div className="small">
                                  <p className="mb-1"><strong>Original Invoice:</strong> {saleInvoice?.order_number?.replace('RET', '')}</p>
                                  <p className="mb-1"><strong>Refund Date:</strong> {dayjs(saleInvoice?.created_at).format('MM-DD-YYYY')}</p>
                                  <p className="mb-0"><strong>Status:</strong> <span className="badge bg-danger">{saleInvoice?.status}</span></p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-start mt-2">
                    <div style={{ maxWidth: '55%' }}>
                      {saleInvoice?.comments && (
                        <p><b>Comments:</b> {saleInvoice?.comments}</p>
                      )}
                      {saleInvoice?.service_type?.remark && (
                        <p>{saleInvoice?.service_type?.remark} {`(charges $${parseFloat(saleInvoice?.service_type?.price).toFixed(2)})`}</p>
                      )}
                      <p className="mt-1">Thank you for shopping at MATTRESS PLUS, you saved <b>${parseFloat(saleInvoice?.total_savings || 0).toFixed(2)}</b></p>
                    </div>

                    <table className="invoice-summary-table" style={{ borderCollapse: 'collapse', minWidth: 220 }}>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: 600, paddingRight: 24, paddingTop: 1, paddingBottom: 1 }}>Sub Total:</td>
                          <td style={{ textAlign: 'right' }}>${(parseFloat(saleInvoice?.sub_total) - (saleInvoice?.total_savings || 0)).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600, paddingRight: 24, paddingTop: 1, paddingBottom: 1 }}>Sale Tax:</td>
                          <td style={{ textAlign: 'right' }}>${parseFloat(saleInvoice?.sales_tax).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 700, paddingRight: 24, paddingTop: 2, paddingBottom: 2, borderTop: '1px solid #333' }}>Grand Total:</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, borderTop: '1px solid #333' }}>${parseFloat(saleInvoice?.total_sale).toFixed(2)}</td>
                        </tr>
                        {saleInvoice?.invoice_payments?.map((payment, index) => (
                          <tr key={index}>
                            <td style={{ fontWeight: 600, paddingRight: 24, paddingTop: 1, paddingBottom: 1 }}>
                              {payment?.payment_head?.name}{payment?.is_voided ? ' (Voided)' : ''}:
                            </td>
                            <td style={{ textAlign: 'right', ...(payment?.is_voided ? { textDecoration: 'line-through', color: '#dc3545' } : {}) }}>
                              ${parseFloat(payment?.received_amount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ fontWeight: 700, paddingRight: 24, paddingTop: 2, borderTop: '1px solid #333' }}>Balance Amount:</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, borderTop: '1px solid #333' }}>${parseFloat(saleInvoice?.balance_amount).toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Linked Transactions Section - Refunds and Exchanges with Tabs */}
        {(!saleInvoice?.order_number?.endsWith('RET') && !saleInvoice?.order_number?.endsWith('EX') && (linkedInvoices.refunds.length > 0 || linkedInvoices.exchanges.length > 0)) && (
          <div className="card-body mb-3">
            <div className="card m-auto" style={{ width: '950px' }}>
              <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                <h5 className="mb-0" style={{ color: '#495057' }}>
                  <i className="fas fa-link me-2"></i>
                  Related Transactions
                </h5>
              </div>
              <div className="card-body">
                <ul className="nav nav-tabs" id="transactionTabs" role="tablist">
                  {linkedInvoices.refunds.length > 0 && (
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="refunds-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#refunds"
                        type="button"
                        role="tab"
                        aria-controls="refunds"
                        aria-selected="true"
                      >
                        <i className="fas fa-undo-alt me-2"></i>
                        Refunds ({linkedInvoices.refunds.length})
                      </button>
                    </li>
                  )}
                  {linkedInvoices.exchanges.length > 0 && (
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${linkedInvoices.refunds.length === 0 ? 'active' : ''}`}
                        id="exchanges-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#exchanges"
                        type="button"
                        role="tab"
                        aria-controls="exchanges"
                        aria-selected={linkedInvoices.refunds.length === 0 ? "true" : "false"}
                      >
                        <i className="fas fa-exchange-alt me-2"></i>
                        Exchanges ({linkedInvoices.exchanges.length})
                      </button>
                    </li>
                  )}
                </ul>

                <div className="tab-content mt-3" id="transactionTabsContent">
                  {/* Refunds Tab */}
                  {linkedInvoices.refunds.length > 0 && (
                    <div
                      className="tab-pane fade show active"
                      id="refunds"
                      role="tabpanel"
                      aria-labelledby="refunds-tab"
                    >
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Receipt ID</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Refund Amount</th>
                              <th>Items</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedInvoices.refunds.map((refund, index) => (
                              <tr key={refund.id}>
                                <td className="fw-bold text-danger">#{refund.receiptId}</td>
                                <td>{dayjs(refund.created_at).format('MM-DD-YYYY HH:mm')}</td>
                                <td>
                                  <span className={`badge ${refund.status === 'processed' ? 'bg-success' : refund.status === 'pending' ? 'bg-warning' : 'bg-secondary'}`}>
                                    {refund.status}
                                  </span>
                                </td>
                                <td className="fw-bold text-danger">${parseFloat(refund.amount || 0).toFixed(2)}</td>
                                <td>{refund.items?.length || 0} item(s)</td>
                                <td>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => navigate(`/refund-receipt/${refund.id}`)}
                                  >
                                    <i className="fas fa-eye me-1"></i>
                                    View Receipt
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Exchanges Tab */}
                  {linkedInvoices.exchanges.length > 0 && (
                    <div
                      className={`tab-pane fade ${linkedInvoices.refunds.length === 0 ? 'show active' : ''}`}
                      id="exchanges"
                      role="tabpanel"
                      aria-labelledby="exchanges-tab"
                    >
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Receipt ID</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Store Credit</th>
                              <th>Amount Due</th>
                              <th>Items</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedInvoices.exchanges.map((exchange, index) => (
                              <tr key={exchange.id}>
                                <td className="fw-bold text-success">#{exchange.receiptId}</td>
                                <td>{dayjs(exchange.created_at).format('MM-DD-YYYY HH:mm')}</td>
                                <td>
                                  <span className={`badge ${exchange.status === 'completed' ? 'bg-success' : exchange.status === 'pending' ? 'bg-warning' : 'bg-secondary'}`}>
                                    {exchange.status}
                                  </span>
                                </td>
                                <td className="fw-bold text-info">${parseFloat(exchange.store_credit_amount || 0).toFixed(2)}</td>
                                <td className="fw-bold text-success">${parseFloat(exchange.total_amount_due || 0).toFixed(2)}</td>
                                <td>{exchange.items?.length || 0} item(s)</td>
                                <td>
                                  <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => navigate(`/exchange-receipt/${exchange.id}`)}
                                  >
                                    <i className="fas fa-eye me-1"></i>
                                    View Receipt
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div >
  );
};

export default SaleInvoice;
